import React, { useState, useEffect } from 'react';
import { CalendarPlus, GitBranch, Loader2, AlertCircle, Unlock, X } from 'lucide-react';
import StatusBadge from './StatusBadge';
import Dialog from './Dialog';
import { api } from '../services/api';
import { currentMonth, isoWeekLabel, monthLabel, monthsOfCycle, todayISO } from '../utils/period';
import { useSystemValues } from '../utils/useSystemValues';
import { confirmNavigateAway } from '../services/dirtyState';
import { appConfirm } from '../services/dialogService';

/**
 * Thanh chọn chu kỳ / bản cập nhật, kèm ba thao tác mà bản cũ không có
 * đường nào bấm được: mở chu kỳ tháng mới, chốt bản cập nhật tuần, và mở
 * lại chu kỳ đã duyệt khi số đã duyệt bị sai.
 *
 * canReopen tách riêng khỏi canEdit vì đây là quyền của người THẨM ĐỊNH:
 * người đã duyệt mới là người được rút lại phê duyệt, không phải người lập
 * kế hoạch. Backend cũng kiểm lại vai trò này, nút chỉ là lớp hiển thị.
 *
 * Đổi chu kỳ / bản cập nhật, mở chu kỳ mới, tạo bản tuần mới đều NẠP LẠI lưới
 * của trang cha -> ô chưa lưu mất sạch mà không ai hỏi (đổi tab/đơn vị thì đã
 * hỏi từ trước). Hỏi ở đây bằng chính confirmNavigateAway, một chỗ cho cả
 * MonthlyForecast lẫn WeeklyForecast (09/10/2026). Select là controlled, nên
 * bấm Huỷ thì giá trị tự quay về lựa chọn cũ.
 */
export default function CycleBar({
  currentBU,
  cycles,
  selectedCycle,
  onSelectCycle,
  versions,
  selectedVersion,
  onSelectVersion,
  canEdit,
  canReopen,
  onChanged
}) {
  const [creating, setCreating] = useState(null); // 'cycle' | 'version' | null
  const [newMonth, setNewMonth] = useState(currentMonth().slice(0, 7));
  const [error, setError] = useState(null);
  const [showReopen, setShowReopen] = useState(false);
  // Số tháng của chu kỳ MỚI do master đặt ở Cài đặt hệ thống (mặc định 4). Chỉ để XEM TRƯỚC: máy chủ tự lấy giá trị này khi tạo chu kỳ.
  const { horizonMonths } = useSystemValues();
  const thangMoi = newMonth ? monthsOfCycle({ base_month: `${newMonth}-01`, horizon_months: horizonMonths }) : [];

  const cycleApproved = selectedCycle?.status === 'approved' || selectedCycle?.status === 'locked';

  // Đổi đơn vị, đổi chu kỳ hoặc đổi bản cập nhật đang chọn KHÔNG unmount
  // component này (cùng 1 CycleBar, chỉ đổi props) — nên lỗi của lần thao tác
  // trước (vd "Chu kỳ ... đã tồn tại" từ một lần bấm "Mở chu kỳ" thất bại) cứ
  // treo mãi trên màn hình dù người dùng đã chuyển sang xem chu kỳ/bản khác,
  // trông như app đang báo nhầm cho lựa chọn hiện tại.
  useEffect(() => {
    setError(null);
  }, [currentBU, selectedCycle?.id, selectedVersion?.id]);

  const createCycle = async () => {
    if (!(await confirmNavigateAway('Mở chu kỳ mới'))) return;
    setError(null);
    setCreating('cycle');
    try {
      const res = await api.createCycle({
        businessUnitCode: currentBU,
        baseMonth: `${newMonth}-01`
        // KHÔNG gửi horizonMonths: máy chủ lấy số tháng từ Cài đặt hệ thống (fc.horizonMonths). Gửi một con số ở đây là ĐÈ lên cài đặt đó.
      });
      await onChanged(res.cycle?.id, res.initialVersionId);
    } catch (err) {
      setError(err.message);
    } finally {
      setCreating(null);
    }
  };

  const createVersion = async () => {
    if (!selectedCycle) return;
    if (!(await confirmNavigateAway('Tạo bản cập nhật tuần mới'))) return;
    // Tạo bản mới là nhân bản toàn bộ số từ bản gần nhất (copyFromPrevious) —
    // bấm nhầm là có ngay một bản cập nhật thừa phải dọn tay, nên hỏi lại
    // giống mẫu Duyệt/Từ chối ở Approvals.jsx.
    if (!(await appConfirm('Tạo bản cập nhật tuần mới, kế thừa số của bản gần nhất?', {
      title: 'Tạo bản cập nhật tuần mới', okLabel: 'Tạo bản mới'
    }))) return;
    setError(null);
    setCreating('version');
    try {
      const nextWeek = versions.length
        ? Math.max(...versions.map((v) => Number(v.update_week) || 0)) + 1
        : 0;
      const res = await api.createVersion(selectedCycle.id, {
        updateWeek: nextWeek,
        updateDate: todayISO(),
        isoWeekLabel: `Tuần ${nextWeek} (${isoWeekLabel()})`,
        copyFromPrevious: true
      });
      await onChanged(selectedCycle.id, res.version?.id);
    } catch (err) {
      setError(err.message);
    } finally {
      setCreating(null);
    }
  };

  const reopen = async (reason) => {
    if (!selectedCycle) return;
    setError(null);
    setCreating('reopen');
    try {
      await api.reopenCycle(selectedCycle.id, reason);
      setShowReopen(false);
      await onChanged(selectedCycle.id, selectedVersion?.id);
    } catch (err) {
      setError(err.message);
    } finally {
      setCreating(null);
    }
  };

  return (
    <div className="bg-white p-3 rounded-xl border border-slate-200 shadow-sm space-y-3">
      <div className="flex flex-col lg:flex-row lg:items-center gap-3">

        {/* Chọn chu kỳ */}
        <div className="flex items-center gap-2">
          <span className="text-xs text-slate-500 whitespace-nowrap">Chu kỳ:</span>
          {cycles.length > 0 ? (
            <select
              value={selectedCycle?.id || ''}
              onChange={async (e) => {
                // Đọc giá trị TRƯỚC await: sau await React đã vẽ lại ô chọn (controlled) về giá trị cũ.
                const id = e.target.value;
                if (await confirmNavigateAway('Đổi chu kỳ')) onSelectCycle(cycles.find((c) => c.id === id));
              }}
              className="bg-slate-50 border border-slate-300 rounded-lg px-3 py-1.5 text-xs font-semibold text-slate-800 outline-none focus:border-blue-500"
            >
              {cycles.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.business_unit_code} · {monthLabel(c.base_month)}
                </option>
              ))}
            </select>
          ) : (
            <span className="text-xs text-slate-500 italic">Chưa có chu kỳ nào cho {currentBU}</span>
          )}
          {selectedCycle && <StatusBadge status={selectedCycle.status} />}
        </div>

        {/* Chọn bản cập nhật */}
        {selectedCycle && (
          <div className="flex items-center gap-2">
            <span className="text-xs text-slate-500 whitespace-nowrap">Bản cập nhật:</span>
            <select
              value={selectedVersion?.id || ''}
              onChange={async (e) => {
                const id = e.target.value;
                if (await confirmNavigateAway('Đổi bản cập nhật')) onSelectVersion(versions.find((v) => v.id === id));
              }}
              className="bg-slate-50 border border-slate-300 rounded-lg px-3 py-1.5 text-xs font-semibold text-slate-800 outline-none focus:border-blue-500"
            >
              {versions.map((v) => (
                <option key={v.id} value={v.id}>
                  {v.iso_week_label || `Tuần ${v.update_week}`}
                  {String(v.is_final) === '1' ? ' — mới nhất' : ''}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Mở lại chu kỳ đã duyệt — quyền của người thẩm định, tách khỏi khối canEdit */}
        {canReopen && cycleApproved && (
          <div className={canEdit ? '' : 'lg:ml-auto'}>
            <button
              onClick={() => { setError(null); setShowReopen(true); }}
              disabled={creating !== null}
              title="Rút lại phê duyệt để sửa số. Bắt buộc nêu lý do và được ghi vào nhật ký."
              className="flex items-center gap-1.5 border border-amber-300 bg-amber-50 hover:bg-amber-100 text-amber-900 px-3 py-1.5 rounded-lg text-xs font-semibold disabled:opacity-50"
            >
              {creating === 'reopen'
                ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                : <Unlock className="w-3.5 h-3.5" />}
              Mở lại chu kỳ
            </button>
          </div>
        )}

        {/* Thao tác */}
        {canEdit && (
          <div className="flex items-center gap-2 lg:ml-auto">
            {selectedCycle && (
              <button
                onClick={createVersion}
                disabled={creating !== null}
                title="Tạo bản cập nhật tuần mới, kế thừa số của bản gần nhất"
                className="flex items-center gap-1.5 border border-slate-300 hover:bg-slate-50 text-slate-700 px-3 py-1.5 rounded-lg text-xs font-semibold disabled:opacity-50"
              >
                {creating === 'version'
                  ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  : <GitBranch className="w-3.5 h-3.5" />}
                Bản cập nhật tuần mới
              </button>
            )}

            <div className="flex items-center gap-1.5">
              <input
                type="month"
                value={newMonth}
                onChange={(e) => setNewMonth(e.target.value)}
                className="bg-slate-50 border border-slate-300 rounded-lg px-2 py-1.5 text-xs outline-none focus:border-blue-500"
              />
              <button
                onClick={createCycle}
                disabled={creating !== null || !newMonth}
                className="flex items-center gap-1.5 border border-slate-300 hover:bg-slate-50 text-slate-700 px-3 py-1.5 rounded-lg text-xs font-semibold disabled:opacity-50"
              >
                {creating === 'cycle'
                  ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  : <CalendarPlus className="w-3.5 h-3.5" />}
                Mở chu kỳ
              </button>
            </div>
          </div>
        )}
      </div>

      {canEdit && thangMoi.length > 0 && (
        <p className="text-[11px] text-slate-500" data-testid="xem-truoc-chu-ky-moi">
          Chu kỳ mới gồm <strong className="text-slate-700">{horizonMonths} tháng</strong>
          {' '}({monthLabel(thangMoi[0])}{thangMoi.length > 1 ? ` → ${monthLabel(thangMoi[thangMoi.length - 1])}` : ''}). Chu kỳ đã tạo giữ nguyên số tháng của nó.
        </p>
      )}

      {error && (
        <div className="bg-rose-50 border border-rose-200 text-rose-800 text-xs rounded-lg p-2.5 flex items-start gap-2">
          <AlertCircle className="w-4 h-4 text-rose-600 flex-shrink-0 mt-0.5" />
          <span>{error}</span>
        </div>
      )}

      {showReopen && (
        <ReopenDialog
          cycleLabel={`${selectedCycle?.business_unit_code} · ${monthLabel(selectedCycle?.base_month)}`}
          busy={creating === 'reopen'}
          onCancel={() => setShowReopen(false)}
          onConfirm={reopen}
        />
      )}
    </div>
  );
}

/** Hỏi lý do trước khi rút lại phê duyệt — backend từ chối nếu để trống. */
function ReopenDialog({ cycleLabel, busy, onCancel, onConfirm }) {
  const [reason, setReason] = useState('');
  const trimmed = reason.trim();

  return (
    <Dialog
      as="form"
      onSubmit={(e) => { e.preventDefault(); if (trimmed) onConfirm(trimmed); }}
      onClose={onCancel}
      busy={busy}
      className="bg-white rounded-xl shadow-2xl w-full max-w-md p-5 space-y-4 text-slate-900"
    >
        <div className="flex items-start justify-between gap-3">
          <div>
            <h3 className="font-bold text-sm">Mở lại chu kỳ đã duyệt</h3>
            <p className="text-xs text-slate-500 mt-0.5">{cycleLabel}</p>
          </div>
          <button type="button" onClick={onCancel} aria-label="Đóng" className="p-1 hover:bg-slate-100 rounded">
            <X className="w-4 h-4 text-slate-500" />
          </button>
        </div>

        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-900 flex items-start gap-2">
          <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
          <span>
            Chu kỳ sẽ quay về trạng thái soạn thảo và cần gửi duyệt lại.
            Bản đã duyệt vẫn được giữ nguyên trong lịch sử, nhưng
            <strong> số liệu sửa sau đây sẽ ghi đè lên bản hiện tại</strong>.
            Lý do được ghi vào nhật ký kèm tên người thực hiện.
          </span>
        </div>

        <div className="space-y-1">
          <label htmlFor="reopen-reason" className="text-xs font-semibold text-slate-700 block">
            Lý do mở lại <span className="text-rose-600">*</span>
          </label>
          <textarea
            id="reopen-reason"
            rows={3}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="VD: Sai sản lượng SKU KAQ-P95 tháng 9, đã đối chiếu lại với kế hoạch sản xuất."
            className="w-full px-3 py-2 bg-slate-50 border border-slate-300 rounded-lg text-xs outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100 resize-none"
          />
        </div>

        <div className="flex gap-2">
          <button
            type="button"
            onClick={onCancel}
            className="flex-1 border border-slate-300 text-slate-700 py-2 rounded-lg text-xs font-semibold hover:bg-slate-50"
          >
            Huỷ
          </button>
          <button
            type="submit"
            disabled={busy || !trimmed}
            className="flex-1 flex items-center justify-center gap-1.5 bg-amber-700 hover:bg-amber-800 disabled:opacity-50 text-white py-2 rounded-lg text-xs font-bold"
          >
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Unlock className="w-3.5 h-3.5" />}
            Mở lại chu kỳ
          </button>
        </div>
    </Dialog>
  );
}
